import { PaymentTypes } from "../../../common/enums";
import { cashChangePaymentTypes } from "../../../common/utilities/paymentUtility";

/** Convert a euro amount to integer cents, avoiding floating-point drift. */
export const toCents = (amount: number): number => Math.round(amount * 100);

/** Convert integer cents back to a euro amount. */
export const fromCents = (cents: number): number => cents / 100;

export interface IPaymentAmountState {
  /** Change owed to the customer (received − total), never negative, in cents. */
  changeCents: number;
  /** Received is less than the total — the payment cannot be completed. */
  isUnderpaid: boolean;
  /** Received is greater than the total — only cash methods can return the change. */
  isOverpaid: boolean;
  /** Received exactly matches the total. */
  isExact: boolean;
}

/** Compares received against total (both in cents) and derives the payment state. */
export const evaluatePaymentAmount = (
  totalCents: number,
  receivedCents: number,
): IPaymentAmountState => {
  const diff = receivedCents - totalCents;
  return {
    changeCents: diff > 0 ? diff : 0,
    isUnderpaid: diff < 0,
    isOverpaid: diff > 0,
    isExact: diff === 0,
  };
};

/** Whether a payment type can physically return change to the customer. */
export const canPaymentTypeGiveChange = (type: PaymentTypes): boolean =>
  cashChangePaymentTypes.includes(type);

/**
 * Whether a payment method may be selected for the given amount state:
 *  • underpaid → nothing is selectable (the customer still owes money)
 *  • overpaid  → only cash-style methods, since they can return change
 *  • exact     → every method is selectable
 */
export const isPaymentMethodSelectable = (
  type: PaymentTypes,
  state: Pick<IPaymentAmountState, "isUnderpaid" | "isOverpaid">,
): boolean => {
  if (state.isUnderpaid) return false;
  if (state.isOverpaid) return canPaymentTypeGiveChange(type);
  return true;
};
